import type { CTRL_SCHEMA } from "@repo/sdk/types";
import { useHttp, type HttpConfiguration } from "@repo/utils/hooks";
import { useCtrlClient } from "../contexts/CtrlApiProvider";

export type Tenant = CTRL_SCHEMA["DbTenant"];

/**
 * Hook for fetching all tenants using SWR
 *
 * @param config - Optional SWR configuration
 * @returns SWR response with tenants data, error, loading states, and mutation function
 *
 * @example
 * ```tsx
 * function TenantList() {
 *   const { data: tenants, error, isLoading, mutate } = useTenants()
 *
 *   if (isLoading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return (
 *     <ul>
 *       {tenants?.map(tenant => (
 *         <li key={tenant.id}>
 *           {tenant.name} - {tenant.oauth_realm}
 *         </li>
 *       ))}
 *     </ul>
 *   )
 * }
 * ```
 */
export function useTenants(config?: HttpConfiguration) {
  const client = useCtrlClient();

  return useHttp<Tenant[]>(
    client.token ? "tenants" : null,
    async () => {
      return await client.getTenants();
    },
    config,
  );
}
